import React, { useRef, useCallback, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Spin } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import { Routes } from '@share/constants';
import { BlueButton } from '@share/components';

import { Banner } from '../components/banner';
import { ImageCarousel } from '../components/image-carousel';
import { StepCard } from '../components/step-card';
import { TravelCard } from '../components/travel-card';
import { CertificateCard } from '../components/certificate-card';
import { DEFAULT_BANNER_BACKGROUND } from '../components/banner/utils';
import { useCertificateTemplates } from '../hooks';
import { LANDING_PAGE_SIZE, STEPS, HOW_IT_WORKS_ITEMS } from './constants';

import styles from './styles.module.scss';

export function CertificatesWrapper(): React.ReactElement {
  const intl = useIntl();
  const carouselSectionRef = useRef<HTMLElement | null>(null);
  const history = useHistory();
  const { accountUsername } = useParams<{ accountUsername?: string }>();

  const { items, totalCount, loading, error, refetch } = useCertificateTemplates({
    pageNumber: 1,
    pageSize: LANDING_PAGE_SIZE,
  });

  const catalogPath = accountUsername
    ? `/${accountUsername}${Routes.CertificatesCatalog}`
    : Routes.CertificatesCatalog;

  const certificateTemplateDetailsPathBase = accountUsername
    ? `/${accountUsername}${Routes.CertificateTemplateDetails}`
    : Routes.CertificateTemplateDetails;

  const certificatePaymentPathBase = accountUsername
    ? `/${accountUsername}${Routes.Certificates}`
    : Routes.Certificates;

  const isScrollToCertificates = useMemo(() => {
    return items.length < 6;
  }, [items.length]);

  const handleScrollToCertificates = useCallback(() => {
    if (!isScrollToCertificates) {
      history.push(catalogPath);
    } else {
      carouselSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isScrollToCertificates, history, catalogPath]);

  const handleCardClick = useCallback(
    (certificateId: string) => {
      history.push(`${certificateTemplateDetailsPathBase}/${certificateId}`);
    },
    [history, certificateTemplateDetailsPathBase],
  );

  const handleBuyCertificate = useCallback(
    (certificateId: string) => {
      history.push(`${certificatePaymentPathBase}/${certificateId}/payment`);
    },
    [history, certificatePaymentPathBase],
  );

  const handleShowAllCertificates = useCallback(() => {
    if (accountUsername) {
      history.push(catalogPath);
    }
  }, [history, accountUsername, catalogPath]);

  const bannerImageAlt = intl.formatMessage({ id: 'certificates.landing.banner.imageAlt' });

  return (
    <div className={styles.wrapper}>
      <Banner
        title={intl.formatMessage({ id: 'certificates.landing.banner.title' })}
        description={intl.formatMessage({ id: 'certificates.landing.banner.description' })}
        buttonLabel={intl.formatMessage({ id: 'certificates.landing.banner.explore' })}
        backgroundImageSrc={DEFAULT_BANNER_BACKGROUND}
        backgroundImageAlt={bannerImageAlt}
        scrollTargetRef={carouselSectionRef}
        onExploreClick={handleScrollToCertificates}
      />

      <section
        className={styles.stepsSection}
        aria-label={intl.formatMessage({ id: 'certificates.landing.stepsSection.aria' })}
      >
        <h2 className={styles.sectionTitle}>
          <FormattedMessage id="certificates.landing.steps.title" />
        </h2>
        <div className={styles.stepsGrid}>
          {STEPS.map((step) => (
            <StepCard
              key={step.stepNumber}
              icon={step.icon}
              stepNumber={step.stepNumber}
              title={intl.formatMessage({ id: step.titleId })}
              description={intl.formatMessage({ id: step.descriptionId })}
            />
          ))}
        </div>
      </section>

      <section
        className={styles.carouselSection}
        aria-label={intl.formatMessage({ id: 'certificates.landing.carousel.aria' })}
      >
        <ImageCarousel
          onExploreClick={isScrollToCertificates ? handleScrollToCertificates : undefined}
        />
      </section>

      <section
        className={styles.howItWorksSection}
        aria-label={intl.formatMessage({ id: 'certificates.landing.howItWorks.aria' })}
      >
        <h2 className={styles.sectionTitle}>
          <FormattedMessage id="certificates.landing.howItWorks.title" />
        </h2>
        <div className={styles.howItWorksGrid}>
          {HOW_IT_WORKS_ITEMS.map((item) => (
            <TravelCard
              key={item.titleId}
              icon={item.icon}
              title={intl.formatMessage({ id: item.titleId })}
              description={intl.formatMessage({ id: item.descriptionId })}
            />
          ))}
        </div>
      </section>

      <section
        ref={carouselSectionRef}
        className={styles.popularSection}
        aria-label={intl.formatMessage({ id: 'certificates.landing.popular.aria' })}
      >
        <div className={styles.popularHeader}>
          <h2 className={styles.sectionTitle}>
            <FormattedMessage id="certificates.landing.popular.title" />
          </h2>
          {totalCount > LANDING_PAGE_SIZE && (
            <a
              href={catalogPath}
              className={styles.viewAllLink}
              onClick={(e) => {
                e.preventDefault();
                handleShowAllCertificates();
              }}
            >
              <FormattedMessage id="certificates.landing.viewAll" />
            </a>
          )}
        </div>

        {loading && (
          <div className={styles.stateBlock} role="status" aria-busy="true">
            <Spin size="large" />
          </div>
        )}

        {!loading && error && (
          <div className={styles.stateBlock}>
            <p className={styles.errorText}>{error}</p>
            <BlueButton onClick={refetch}>
              <FormattedMessage id="certificates.landing.retry" />
            </BlueButton>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className={styles.stateBlock}>
            <p className={styles.emptyText}>
              <FormattedMessage id="certificates.landing.empty" />
            </p>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className={styles.certificatesGrid}>
              {items.map((item) => (
                <CertificateCard
                  key={item.id}
                  item={item}
                  imageUrl={item.imageUrl}
                  onCardClick={() => handleCardClick(item.id)}
                  onBuyCertificate={() => handleBuyCertificate(item.id)}
                />
              ))}
            </div>
            {totalCount > LANDING_PAGE_SIZE && (
              <div className={styles.showAllWrap}>
                <BlueButton className={styles.showAllButton} onClick={handleShowAllCertificates}>
                  <FormattedMessage id="certificates.landing.showAll" />
                </BlueButton>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
